import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DatePickerModule } from 'primeng/datepicker';
import { TranslateModule } from '@ngx-translate/core';
import { ChipModule } from 'primeng/chip';

/**
 * Color palette for multi-year comparison - must match YEAR_COLOR_PALETTE in multi-year-data.manager.ts
 */
export const YEAR_CHIP_COLORS: string[] = [
  '#ef4444', // red
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

/**
 * Year Multi Picker Component
 * Allows selecting multiple years for comparison charts
 * Extends the logic of DatePickerComponent but specialized for year selection
 */
@Component({
  selector: 'app-year-multi-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePickerModule, TranslateModule, ChipModule],
  templateUrl: './year-multi-picker.component.html',
  styleUrls: ['./year-multi-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => YearMultiPickerComponent),
      multi: true
    }
  ]
})
export class YearMultiPickerComponent implements ControlValueAccessor {

  /** Internal value as array of years (numbers) */
  selectedYears: number[] = [];

  /** Date value for the picker (single selection mode for adding years) */
  pickerValue: Date | null = null;

  private onTouchedCallback: () => void = () => {};
  private onChangeCallback: (_: number[]) => void = () => {};

  @Input() placeholder: string = 'SELECT_YEAR';
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() showIcon: boolean = true;
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() maxYears: number = 5; // Maximum number of years that can be selected

  @Output() onYearsChange = new EventEmitter<number[]>();


  writeValue(value: number[] | null): void {
    this.selectedYears = value || [];
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Called when a year is selected from the picker
   */
  onYearSelect(date: Date | null): void {
    if (!date) return;

    const year = date.getFullYear();

    // Check if year is already selected
    if (this.selectedYears.includes(year)) {
      return;
    }

    // Check max years limit
    if (this.selectedYears.length >= this.maxYears) {
      // Remove the oldest year and add the new one
      this.selectedYears = [...this.selectedYears.slice(1), year];
    } else {
      this.selectedYears = [...this.selectedYears, year];
    }

    // Sort years descending (most recent first)
    this.selectedYears.sort((a, b) => b - a);

    // Reset picker value
    this.pickerValue = null;

    this.emitChange();
  }

  /**
   * Remove a year from selection
   */
  removeYear(year: number): void {
    this.selectedYears = this.selectedYears.filter(y => y !== year);
    this.emitChange();
  }

  /**
   * Get color for a specific year based on its index
   */
  getYearColor(year: number): string {
    const index = this.selectedYears.indexOf(year);
    return YEAR_CHIP_COLORS[index % YEAR_CHIP_COLORS.length];
  }

  /**
   * Get color for a year by its index in selection
   */
  getColorByIndex(index: number): string {
    return YEAR_CHIP_COLORS[index % YEAR_CHIP_COLORS.length];
  }

  onBlur(): void {
    this.onTouchedCallback();
  }

  private emitChange(): void {
    this.onChangeCallback(this.selectedYears);
    this.onYearsChange.emit(this.selectedYears);
  }

  /**
   * Check if a year is already selected (for disabling in picker)
   */
  isYearSelected(year: number): boolean {
    return this.selectedYears.includes(year);
  }
}
